import React from "react";
import { Subscribe, Container } from "unstated";
import { produce } from "immer";
import fetch from "mibaby-common/util/fetch";

/**
 * Splits an id (e.g item_32) and returns the second numeric part (as string!)
 * @param {string} i
 * @return {string}
 */
export const splitId = i => i && i.split("_")[1];

function flatItems(items) {
    return Object.values(items).reduce((items, item) => {
        if (item.hasOwnProperty("items")) {
            items.push(flatItems(item.items));
        } else {
            items.push(splitId(item.id));
        }
        return items;
    }, []);
}

function getRegistry(stateItems) {
    return Object.values(stateItems).reduce((ar, item) => {
        if (item.view_widget === "gallery") {
            ar = item.items.reduce((ar, galleryItem, galleryIndex) => {
                const newEntry = {
                    item: { ...galleryItem },
                    container: item,
                    containerIndex: galleryIndex,
                    globalIndex: ar.length,
                };
                ar.push(newEntry);
                return ar;
            }, ar);
        } else {
            ar.push({
                item,
                container: null,
                containerIndex: -1,
                globalIndex: ar.length,
            });
        }
        return ar;
    }, []);
}

function findItem(idOrItem, stateItems) {
    const registry = getRegistry(stateItems);
    return registry.find(i => (typeof idOrItem === "object" ? i.item.id === idOrItem.id : i.item.id === idOrItem));
}

const onceWithAbort = fn => {
    let currentController = null;
    async function createRequest(request, options = {}) {
        if (currentController) {
            currentController.abort();
        }
        currentController = new AbortController();
        const signal = currentController.signal;
        try {
            return await fetch(request, { ...options, signal });
        } catch (err) {
            if (err.name === "AbortError") {
                console.log("Fetch aborted");
            } else {
                console.error("Uh oh, an error!", err);
            }
        }
        return null;
    }

    return (...args) => {
        return fn(...args, createRequest);
    };
};

const apiSavePositions = onceWithAbort(async (items, fetch) => {
    return await fetch("/content_feeds/reposition", {
        method: "POST",
        body: JSON.stringify({ items: flatItems(items) }),
    });
});

const apiAssociate = async (item, feed_id) => {
    const response = await fetch("/content_feeds/add_new", {
        method: "POST",
        body: JSON.stringify({ teaser_id: splitId(item.teaser_id), feed_id }),
    });
    const { id } = await response.json();
    return id;
};

const apiCreate = async (item, feed_id) => {
    if (item.view_widget === "gallery_item" && !item.group) {
        item.group = prompt("Bitte Gruppen-Name für Gallery eingeben");
    }
    const model = new miBaby.app.content_teasers.ContentTeaser(item);
    await model.save();

    return `teaser_${model.id}`;
};

const apiSaveItem = async (action, model, teaserId, item = {}) => {
    switch (action) {
        case "new":
            item = new miBaby.app.content_feed.items.ContentFeedItem(item);

            const attrs = { ...model.attributes, copied_from: teaserId, id: undefined, created: undefined, modified: undefined };

            const newModel = new miBaby.app.content_teasers.ContentTeaser(attrs);
            await newModel.save();

            item.set({ content_teaser_id: newModel.id });
            await item.save();

            break;
        case "old":
            await model.save();
            break;
    }

    return fetch(`/content_feeds/single/${item.id}`).then(res => res.json());
};

export async function apiLibrary(query = {}) {
    const response = await fetch(`/content_feeds/library`, {
        method: "POST",
        body: JSON.stringify({ query }),
    });

    return await response.json();
}

class AdminFeedContainer extends Container {
    teaserApp = new miBaby.app.content_teasers.ContentTeaserApp({});
    itemApp = new miBaby.app.content_feed.items.ContentFeedItemForeignApp({});
    feedApp = new miBaby.app.content_feed.ContentFeedForeignApp({});

    constructor(props) {
        super(props);
        this.state.feed_id = props.feed_id;
        this.state.items = props.items.reduce((o, item) => ((o[item.id] = item), o), {});
    }

    state = {
        feed_id: null,
        dragging: false,
        showToolbar: false,
        items: {},
        library: {},
        flatItems: {},
    };

    openToolbar = () => {
        this.setState({
            showToolbar: !this.state.dragging,
        });
    };

    closeToolbar = () => {
        this.setState({
            showToolbar: false,
        });
    };

    beginDrag = () => {
        this.setState({ dragging: true });
        // we do not know the reason right now, but this fixes it.
        // it probably has to do with the fact that the GUI hides
        // right after dragging starts. I guess it has something to do
        // with DnD and the HTML5 DragSource. But it stops working
        // after we migrated to babel 7 ... mibaby/mibaby-plan#192
        setTimeout(() => {
            this.setState(state => {
                if (state.dragging === true) {
                    return { showToolbar: false };
                }
            });
        }, 500);
    };

    endDrag = () => {
        this.setState({ dragging: false });
    };

    editPage = async () => {
        const { feed_id } = this.state;
        await this.feedApp.promptEdit(feed_id);
    };

    editGalleryItem = async (item, parentGalleryItem) => {
        const itemId = splitId(item.id);
        const teaserId = splitId(item.teaser_id);

        const [action, model] = await this.teaserApp.promptEditFeed(teaserId, itemId);

        const { id, ...newData } = await apiSaveItem(action, model, teaserId, { id: itemId });

        await this.setState(
            produce(draft => {
                const galleryRoot = draft.items[parentGalleryItem.id];
                const galleryItem = galleryRoot.items.find(i => i.id === item.id);
                const index = galleryRoot.items.indexOf(galleryItem);

                Object.assign(galleryRoot.items[index], newData);
            })
        );

        // // If a new instance of a teaser is created, it will be
        // // appended at the bottom of the database, but
        // // we have it already sorted in the gallery, so save
        // // the positions as well.
        // apiSavePositions(this.state.items);
    };

    editItem = async item => {
        const itemId = splitId(item.id);
        const teaserId = splitId(item.teaser_id);

        const [action, model] = await this.teaserApp.promptEditFeed(teaserId, itemId);

        const { id, ...newData } = await apiSaveItem(action, model, teaserId, { id: itemId });

        await this.setState(
            produce(({ items }) => {
                Object.assign(items[item.id], newData);
            })
        );
    };

    removeItem = async item => {
        const items = item.hasOwnProperty("items") ? item.items : [item];

        this.setState(
            produce(draft => {
                delete draft.items[item.id];
            })
        );

        await Promise.all(
            items.map(({ id }) => {
                const model = new miBaby.app.content_feed.items.ContentFeedItem({
                    id: splitId(id),
                });
                return model.destroy();
            })
        );
    };

    removeGalleryItem = async (item, parentGalleryItem) => {
        const removePromise = this.setState(
            produce(draft => {
                const items = draft.items[parentGalleryItem.id].items;
                const index = items.findIndex(i => i.id === item.id);
                items.splice(index, 1);
            })
        );

        const model = new miBaby.app.content_feed.items.ContentFeedItem({
            id: splitId(item.id),
        });

        await Promise.all([removePromise, model.destroy()]);
    };

    cloneItem = async item => {
        const itemId = splitId(item.id);
        const teaserId = splitId(item.teaser_id);

        const teaserModel = await this.teaserApp.ensureModel(teaserId);
        if (teaserModel.get("system_name")) return alert("Cannot clone teaser with system_name.");

        const itemModel = await this.itemApp.ensureModel(itemId);
        const sort = Number(itemModel.get("sort") || 0) + 1;

        const newData = await apiSaveItem("new", teaserModel, teaserId, { content_feed_id: this.state.feed_id });

        await this.updateItems(values => (values.splice(sort, 0, newData), values));
        apiSavePositions(this.state.items);
    };

    cloneGalleryItem = (item, parentGalleryItem) => {
        alert("Cloning gallery items is not implemented yet.");
    };

    addNew = async (item, position) => {
        const hasChildren = item.hasOwnProperty("items");
        let items = hasChildren ? item.items : [item];

        // Create children
        items = await Promise.all(items.map(async item => (item.teaser_id ? item : { ...item, teaser_id: await apiCreate(item, this.state.feed_id) })));

        // associate to feed
        const ids = await Promise.all(items.map(item => apiAssociate(item, this.state.feed_id)));

        let result = { ...item };

        if (hasChildren) {
            result.id = "gallery_" + splitId(ids[0]);
            result.items = result.items.map((item, i) => {
                return {
                    ...item,
                    id: ids[i],
                    teaser_id: items[i].teaser_id,
                };
            });
        } else {
            result.id = ids[0];
            result.teaser_id = items[0].teaser_id;
        }

        await this.updateItems(values => (values.splice(position, 0, result), values));
        apiSavePositions(this.state.items);
    };

    addNewGalleryItem = async (galleryItem, item, position) => {
        if (!item.teaser_id) {
            item = {
                ...item,
                teaser_id: await apiCreate(
                    {
                        ...item,
                        sort: splitId(galleryItem.id) + "_" + position,
                    },
                    this.state.feed_id
                ),
            };
        }

        const id = await apiAssociate(item, this.state.feed_id);
        const result = { ...item, id };

        await this.setState(
            produce(draft => {
                const items = draft.items[galleryItem.id].items;
                items.splice(position, 0, result);
            })
        );

        apiSavePositions(this.state.items);
    };

    reposition = async (item, index) => {
        const items = Object.values(this.state.items);
        const currentIndex = items.indexOf(item);

        items.splice(currentIndex, 1);
        items.splice(index, 0, item);

        return Promise.all([this.resetItems(items), apiSavePositions(items)]);
    };

    repositionGalleryItem = async (gallery, item, position) => {
        const registryItem = findItem(item, this.state.items);
        if (registryItem.container.id !== gallery.id) {
            await this.setState(
                produce(draft => {
                    const sourceItems = draft.items[registryItem.container.id].items;
                    sourceItems.splice(registryItem.containerIndex, 1);
                    const targetItems = draft.items[gallery.id].items;
                    targetItems.splice(position, 0, item);
                })
            );
        } else {
            await this.setState(
                produce(draft => {
                    const galleryItems = draft.items[gallery.id].items;
                    galleryItems.splice(registryItem.containerIndex, 1);
                    galleryItems.splice(position, 0, item);
                })
            );
        }

        apiSavePositions(this.state.items);
    };

    updateItems = fn => {
        const values = fn(Object.values(this.state.items));
        return this.resetItems(values);
    };

    async resetItems(items) {
        return await this.setState({
            items: items.reduce((o, item) => ((o[item.id] = item), o), {}),
        });
    }
}

const AdminFeedSubscribe = ({ children }) => <Subscribe to={[AdminFeedContainer]}>{container => children(container)}</Subscribe>;

const subscribe = (WrappedComponent, map = null) => {
    const comp = props => (
        <AdminFeedSubscribe>
            {container => {
                if (map) {
                    return <WrappedComponent {...map(container)} {...props} />;
                }
                return <WrappedComponent container={container} {...props} />;
            }}
        </AdminFeedSubscribe>
    );

    comp.displayName = `AdminFeedSubscribed(${WrappedComponent.displayName})`;
    return comp;
};

AdminFeedSubscribe.displayName = "AdminFeedSubscribe";

export default AdminFeedSubscribe;

export { AdminFeedContainer, subscribe };
